import { OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { debounceTime, map, tap } from 'rxjs';
import {
  DEBOUNCE_MS,
  MIN_INPUT_LENGTH,
  WILDCARD_SEARCH_TERM,
} from '../../constants';
import { SearchState } from '../../store/search.state';
import { SearchOverlay } from '../search-overlay/search-overlay.component';

@Component({
  selector: 'procure-search',
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatIcon,
    SearchOverlay,
    OverlayModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Search {
  readonly input = viewChild('input', { read: ElementRef });
  readonly triggerRef = viewChild('trigger', { read: ElementRef });

  protected readonly searchControl = new FormControl<string | null>(null);
  protected readonly state = inject(SearchState);

  protected readonly autocomplete = computed(
    () => this.state.autocomplete()[0],
  );

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        map((term) => term?.trim() ?? ''),
        tap(() => this.state.clearAutocomplete()),
        debounceTime(DEBOUNCE_MS),
        takeUntilDestroyed(),
      )
      .subscribe((value) => {
        this.state.searchTerm.set(value);
        this.search();
      });

    effect(() => {
      if (this.state.searchVisible()) {
        return;
      }

      this.searchControl.setValue('', { emitEvent: false });
    });
  }

  clearInput(): void {
    this.searchControl.patchValue(null);
    this.state.searchTerm.set('');
    this.state.data.set(null);
  }

  protected search(): void {
    const { value } = this.searchControl;

    this.state.fetchAutocomplete();

    if (
      !value ||
      (value.length < MIN_INPUT_LENGTH && value !== WILDCARD_SEARCH_TERM)
    ) {
      return;
    }

    const isSearchVisible = untracked(() => this.state.searchVisible());

    if (!isSearchVisible) {
      this.state.searchVisible.set(true);
    }

    this.state.fetchData();
  }

  protected clear(event?: MouseEvent): void {
    if (
      event?.target &&
      this.triggerRef()?.nativeElement.contains(event.target as Node)
    ) {
      return;
    }
    this.input()?.nativeElement.blur();
    this.state.hideSearch();
  }

  protected acceptAutocomplete(event: Event): void {
    const autocomplete = this.autocomplete();

    if (!autocomplete) {
      return;
    }

    event.preventDefault();

    this.searchControl.setValue(autocomplete);
  }
}
